import { useApi } from '@backstage/core-plugin-api';
import type { FieldExtensionComponentProps } from '@backstage/plugin-scaffolder-react';
import Autocomplete from '@material-ui/lab/Autocomplete';
import Box from '@material-ui/core/Box';
import CircularProgress from '@material-ui/core/CircularProgress';
import TextField from '@material-ui/core/TextField';
import Typography from '@material-ui/core/Typography';
import { useEffect, useState } from 'react';
import {
  naptaProjectsApiRef,
  type NaptaProject,
} from '../../api/naptaProjectsApi';

export interface NaptaProjectValue {
  project_id: string;
  project_name: string;
}

// Bounds the dropdown height so long project lists stay scrollable instead of filling the page.
const LISTBOX_PROPS = { style: { maxHeight: 280 } };

export const NaptaProjectPicker = ({
  formData,
  onChange,
  rawErrors,
  required,
  disabled,
  readonly,
}: FieldExtensionComponentProps<NaptaProjectValue>) => {
  const api = useApi(naptaProjectsApiRef);
  const [projects, setProjects] = useState<NaptaProject[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | undefined>();

  useEffect(() => {
    let cancelled = false;

    api
      .listProjects()
      .then(result => {
        if (!cancelled) setProjects(result);
      })
      .catch(err => {
        if (!cancelled) setError(err);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [api]);

  if (loading) {
    return (
      <Box display="flex" alignItems="center" gridGap={8}>
        <CircularProgress size={20} />
        <Typography variant="body2">Loading projects...</Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Typography variant="body2" color="error">
        Unable to load Napta projects.
      </Typography>
    );
  }

  if (projects.length === 0) {
    return (
      <Typography variant="body2">No Napta projects available.</Typography>
    );
  }

  const selected =
    projects.find(project => project.project_id === formData?.project_id) ??
    projects.find(project => project.project_name === formData?.project_name) ??
    null;

  const hasError = Boolean(rawErrors?.length);
  const isDisabled = disabled || readonly;

  const selectProject = (project: NaptaProject | null) => {
    if (!project) return;
    onChange({
      project_id: project.project_id,
      project_name: project.project_name,
    });
  };

  return (
    <Box display="flex" flexDirection="column" gridGap={16}>
      <Autocomplete<NaptaProject>
        options={projects}
        getOptionLabel={project => project.project_id}
        getOptionSelected={(option, value) =>
          option.project_id === value.project_id
        }
        value={selected}
        onChange={(_event, project) => selectProject(project)}
        disabled={isDisabled}
        ListboxProps={LISTBOX_PROPS}
        renderInput={params => (
          <TextField
            {...params}
            label="Project ID"
            required={required}
            error={hasError}
            helperText={hasError ? rawErrors?.join(', ') : undefined}
          />
        )}
      />

      <Autocomplete<NaptaProject>
        options={projects}
        getOptionLabel={project => project.project_name}
        getOptionSelected={(option, value) =>
          option.project_id === value.project_id
        }
        value={selected}
        onChange={(_event, project) => selectProject(project)}
        disabled={isDisabled}
        ListboxProps={LISTBOX_PROPS}
        renderInput={params => (
          <TextField
            {...params}
            label="Project Name"
            required={required}
            error={hasError}
          />
        )}
      />
    </Box>
  );
};
